/**
 * Canonical external Supabase client (DB + Auth + Edge Functions).
 * Import `{ supabase }` from "@/lib/supabase".
 */
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import {
  EXTERNAL_SUPABASE_URL,
  EXTERNAL_SUPABASE_ANON_KEY,
  EXTERNAL_SUPABASE_AUTH_STORAGE_KEY,
} from "@/config/supabase";
import { createActiveOrgFetch } from "@/lib/active-workspace";

const authStorage = (() => {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
})();

export const supabase = createClient<Database>(
  EXTERNAL_SUPABASE_URL,
  EXTERNAL_SUPABASE_ANON_KEY,
  {
    auth: {
      storageKey: EXTERNAL_SUPABASE_AUTH_STORAGE_KEY,
      ...(authStorage ? { storage: authStorage } : {}),
      persistSession: true,
      autoRefreshToken: true,
    },
    global: {
      fetch: createActiveOrgFetch(),
    },
  }
);

const PASSWORD_RECOVERY_KEY = "flow:password-recovery";
let passwordRecoveryPending = false;

function rememberPasswordRecovery() {
  passwordRecoveryPending = true;
  try {
    window.sessionStorage.setItem(PASSWORD_RECOVERY_KEY, "1");
  } catch {
    return;
  }
}

if (typeof window !== "undefined" && window.location.hash.includes("type=recovery")) {
  rememberPasswordRecovery();
}

supabase.auth.onAuthStateChange((event) => {
  if (event === "PASSWORD_RECOVERY") rememberPasswordRecovery();
  if (event === "SIGNED_OUT") clearPasswordRecovery();
});

export function isPasswordRecoveryPending(): boolean {
  if (passwordRecoveryPending) return true;
  try {
    return window.sessionStorage.getItem(PASSWORD_RECOVERY_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearPasswordRecovery() {
  passwordRecoveryPending = false;
  try {
    window.sessionStorage.removeItem(PASSWORD_RECOVERY_KEY);
  } catch {
    return;
  }
}
